import { Component, OnInit } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { IGridResult } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { IWeekReportingObject, IWeekReportingObjectSearchRequest } from '../../models/week-reporting-object';
import { TimeEntryService } from '../../services/time-entry.service';
import { AsyncPipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UntypedFormBuilder } from '@angular/forms';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { IDayReportingObject, IDayReportingObjectSearchRequest, IExportDayReportingObjectRequest } from '../../models/day-reporting-object';
import { ISearchTimeEntriesRequest, ITimeEntry } from '../../models/time-entry';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { Store } from '@ngrx/store';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';

type ViewMode = 'week' | 'day' | 'entry';

@UntilDestroy()
@Component({
    selector: 'app-my-time-reporting',
    templateUrl: './my-time-reporting.component.html',
    styleUrls: ['./my-time-reporting.component.css'],
    standalone: false
})
export class MyTimeReportingComponent implements OnInit {

  constructor(
    private timeEntryService: TimeEntryService,
    private fb: UntypedFormBuilder,
    private store: Store<AppStateInterface>,
    private tenantSettingsStore: TenantSettingsStoreService,
    private permissionStorage: PermissionStorageService
  ) { }

  // The endpoint demands the export right; the server lets everybody export their own reports.
  canExport = (this.permissionStorage.getPermissions() ?? [])
    .includes('Permissions.DayReportingObject.Export');

  // Without any of the rights for one's own entries the entry view stays a plain list.
  canWriteOwnEntries = ['CreateSelf', 'UpdateSelf', 'DeleteSelf']
    .some(action => (this.permissionStorage.getPermissions() ?? []).includes(`Permissions.TimeEntries.${action}`));

  public currentEmployee$ : Observable<IEmployee> =
  this.store.select(currentEmployeeSelector);

  weekReportingObjectsGridData: IGridResult<IWeekReportingObject[]> = {
    data : [],
    total: 0
  };

  // Flat list of all day reports across the loaded weeks, used by the "Tagesrapport" view.
  dayReportingObjectsGridData: IDayReportingObject[] = [];

  // Single time entries for the entry view, the place to add, change and delete one's own.
  timeEntriesGridData: IGridResult<ITimeEntry[]> = {
    data: [],
    total: 0
  };

  entryTotalTime: string;
  entryGrandTotalTime: string;

  // View toggle between the weekly ('week'), the flat daily ('day') and the single entry
  // ('entry') report view.
  viewMode: ViewMode = 'week';

  // Grid footer totals (work time / flex time / pre-work time) per view.
  weekTotalWorkTime: string;
  weekTotalFlexTime: string;
  weekTotalPreWorkTime: string;
  weekGrandWorkTime: string;
  weekGrandFlexTime: string;
  weekGrandPreWorkTime: string;
  dayTotalWorkTime: string;
  dayTotalFlexTime: string;
  dayTotalPreWorkTime: string;

  // Grand totals over the whole selection (all pages), from the day report response.
  dayGrandWorkTime: string;
  dayGrandFlexTime: string;
  dayGrandPreWorkTime: string;

  ngOnInit(): void {
    this.currentEmployee$.pipe(untilDestroyed(this)).subscribe(employee => {
      this.searchRequest.employeeId = employee.id;
      this.daySearchRequest.employeeId = employee.id;
      this.entrySearchRequest.employeeId = employee.id;
      this.updateList()
    })
  }

  // The search buttons show their spinner for the endpoint of the active view.
  get searchLoadingUrl(): string {
    switch(this.viewMode){
      case 'day': return '/TimeEntry/searchDayReports';
      case 'entry': return '/TimeEntry/searchTimeEntries';
      default: return '/TimeEntry/searchWeekReports';
    }
  }

  entrySearchRequest: ISearchTimeEntriesRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy: ['Date desc','Start']
  }

  searchRequest: IWeekReportingObjectSearchRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy:['StartDate desc']
  }

  // The daily view queries day reports directly so the date filter matches individual
  // days (searchWeekReports only returns weeks fully inside the range) and is server-paged
  // like the weekly view.
  daySearchRequest: IDayReportingObjectSearchRequest = {
    employeeId: null,
    startDate: null,
    endDate: null,
    showOnlyConfirmable: false,
    pageNumber: 1,
    pageSize: 25,
    orderBy: ['Date desc']
  }

  dayTotalCount: number = 0;

  filterForm = this.fb.group({
    dateRangeStart:[],
    dateRangeEnd:[],
    showOnlyConfirmable:[false]
  })

    vm$ = combineLatest([
      this.tenantSettingsStore.tenantSettings$
    ]).pipe(
      map(([
        tenantSettings
      ]) => ({
        tenantSettings
      }))
    );

  updateList(){
    if(this.viewMode === 'day'){
      this.updateDayList();
    } else if(this.viewMode === 'entry'){
      this.updateEntryList();
    } else {
      this.updateWeekList();
    }
  }

  updateEntryList(){
    this.timeEntryService.searchTimeEntries(this.entrySearchRequest)
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.timeEntriesGridData.data = response.data;
      this.timeEntriesGridData.total = response.totalCount;

      // Page total (visible page) + grand total over the whole selection (backend).
      this.entryTotalTime = this.sumTimes(response.data.map(timeEntry => timeEntry.duration));
      this.entryGrandTotalTime = response.totalTime;
    })
  }

  updateWeekList(){
    this.timeEntryService.searchWeekReports(this.searchRequest)
    .pipe(
      untilDestroyed(this),
      map(response => {
        const tmp = response;

        tmp.data.map(
          weekReportObject => {

            weekReportObject.dayReportingObjects = weekReportObject.dayReportingObjects.map((dayReportingObject: IDayReportingObject) => {
              dayReportingObject.date = new Date(dayReportingObject.date);
              return dayReportingObject;
            });

            weekReportObject.dayReportingObjects = weekReportObject.dayReportingObjects.sort((a, b) => b.date.getTime()- a.date.getTime());

            weekReportObject.dayReportingObjects.map(dayReportObject => {
              let totalDuration = new TimeSpan();

              dayReportObject.timeEntries = dayReportObject.timeEntries.sort((a,b) => {
                const timeSpanA = TimeSpan.parse(a.start).getTotalSeconds();
                const timeSpanB = TimeSpan.parse(b.start).getTotalSeconds();

                return timeSpanA - timeSpanB;
              })

              dayReportObject.timeEntries.forEach(timeEntry => {
                if(timeEntry.duration){
                  const timeSpan = TimeSpan.parse(timeEntry.duration)
                  totalDuration = totalDuration.add(timeSpan)
                }
              });
              

              dayReportObject.duration = totalDuration.toStringShort();

              dayReportObject.flexTimeChangeObj = TimeSpan.parse(dayReportObject.flexTimeChange ?? "00:00:00").getAsTimeObject();
              dayReportObject.preWorkTimeChangeObj = TimeSpan.parse(dayReportObject.preWorkTimeChange ?? "00:00:00").getAsTimeObject();
              dayReportObject.overtimeChangeObj = TimeSpan.parse(dayReportObject.overtimeChange ?? "00:00:00").getAsTimeObject();
            })
            weekReportObject.overtimeObj = TimeSpan.parse(weekReportObject.overtime ?? "00:00:00").getAsTimeObject();
            weekReportObject.flexTimeChangeObj = TimeSpan.parse(weekReportObject.flexTimeChange).getAsTimeObject();
            weekReportObject.preWorkTimeChangeObj = TimeSpan.parse(weekReportObject.preWorkTimeChange).getAsTimeObject();
            weekReportObject.timeSupplementTimeChangeObj = TimeSpan.parse(weekReportObject.timeSupplementTimeChange).getAsTimeObject();
            weekReportObject.totalTimeWorkedObj = TimeSpan.parse(weekReportObject.totalTimeWorked).getAsTimeObject();
          }
        )

        return tmp;
      }))
    .subscribe(response => {
      this.weekReportingObjectsGridData.data = response.data;
      this.weekReportingObjectsGridData.total = response.totalCount;

      // Page totals sum the visible week rows.
      this.weekTotalWorkTime = this.sumTimes(response.data.map(week => week.totalTimeWorked));
      this.weekTotalFlexTime = this.sumTimes(response.data.map(week => week.flexTimeChange));
      this.weekTotalPreWorkTime = this.sumTimes(response.data.map(week => week.preWorkTimeChange));

      // Grand totals over the whole selection come from the backend (all pages).
      this.weekGrandWorkTime = response.totalTimeWorked;
      this.weekGrandFlexTime = response.flexTimeChange;
      this.weekGrandPreWorkTime = response.preWorkTimeChange;
    })
  }

  updateDayList(){
    this.timeEntryService.searchDayReports(this.daySearchRequest)
    .pipe(
      untilDestroyed(this),
      map(response => {
        response.data.forEach(dayReportObject => {
          dayReportObject.date = new Date(dayReportObject.date);

          let totalDuration = new TimeSpan();

          dayReportObject.timeEntries = dayReportObject.timeEntries.sort((a, b) => {
            return TimeSpan.parse(a.start).getTotalSeconds() - TimeSpan.parse(b.start).getTotalSeconds();
          });

          dayReportObject.timeEntries.forEach(timeEntry => {
            if(timeEntry.duration){
              totalDuration = totalDuration.add(TimeSpan.parse(timeEntry.duration));
            }
          });

          dayReportObject.duration = totalDuration.toStringShort();

          dayReportObject.flexTimeChangeObj = TimeSpan.parse(dayReportObject.flexTimeChange ?? "00:00:00").getAsTimeObject();
          dayReportObject.preWorkTimeChangeObj = TimeSpan.parse(dayReportObject.preWorkTimeChange ?? "00:00:00").getAsTimeObject();
          dayReportObject.timeSupplementTimeChangeObj = TimeSpan.parse(dayReportObject.timeSupplementTimeChange ?? "00:00:00").getAsTimeObject();
          dayReportObject.overtimeChangeObj = TimeSpan.parse(dayReportObject.overtimeChange ?? "00:00:00").getAsTimeObject();
        });

        return response;
      })
    )
    .subscribe(response => {
      // "Bestätigte Tage ausblenden" is applied server-side so paging stays consistent.
      this.dayReportingObjectsGridData = response.data;
      this.dayTotalCount = response.totalCount;

      // Footer totals sum the current page (consistent with the weekly view).
      this.dayTotalWorkTime = this.sumTimes(response.data.map(day => day.duration));
      this.dayTotalFlexTime = this.sumTimes(response.data.map(day => day.flexTimeChange));
      this.dayTotalPreWorkTime = this.sumTimes(response.data.map(day => day.preWorkTimeChange));

      // Grand totals over the whole selection come from the backend (all pages).
      this.dayGrandWorkTime = response.totalTimeWorked;
      this.dayGrandFlexTime = response.flexTimeChanges;
      this.dayGrandPreWorkTime = response.preWorkTimeChanges;
    })
  }

  private sumTimes(values: (string | undefined | null)[]): string {
    let total = new TimeSpan();
    values.forEach(value => {
      total = total.add(TimeSpan.parse(value ?? "00:00:00"));
    });
    return total.toString();
  }

  onViewModeChange(viewMode: ViewMode){
    this.viewMode = viewMode;

    // The grid of the incoming view is created fresh and starts on its first page,
    // so the search request has to start there as well.
    this.entrySearchRequest.pageNumber = 1;

    this.updateList();
  }

  onSubmitFilter(){
    const formValues = this.filterForm.value;
    const startDate = formValues.dateRangeStart
      ? new Date(formValues.dateRangeStart.getTime() - formValues.dateRangeStart.getTimezoneOffset() * 60000)
      : null;
    const endDate = formValues.dateRangeEnd
      ? new Date(formValues.dateRangeEnd.getTime() - formValues.dateRangeEnd.getTimezoneOffset() * 60000)
      : null;

    this.searchRequest.startDate = startDate;
    this.searchRequest.endDate = endDate;
    this.searchRequest.showOnlyConfirmable = formValues.showOnlyConfirmable ?? false;
    this.searchRequest.pageNumber = 1;

    this.daySearchRequest.startDate = startDate;
    this.daySearchRequest.endDate = endDate;
    this.daySearchRequest.showOnlyConfirmable = formValues.showOnlyConfirmable ?? false;
    this.daySearchRequest.pageNumber = 1;

    this.entrySearchRequest.dateFrom = startDate;
    this.entrySearchRequest.dateTo = endDate;
    this.entrySearchRequest.pageNumber = 1;

    this.updateList();
  }

  onExport(){
    const request: IExportDayReportingObjectRequest = {
      employeeId: this.daySearchRequest.employeeId,
      startDate: this.daySearchRequest.startDate,
      endDate: this.daySearchRequest.endDate
    };

    this.timeEntryService.exportDevices(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => downloadBlob(response, "TagesrapporteExport.xlsx"));
  }

  onResetFilter(){
    this.filterForm.reset();

    this.searchRequest.pageNumber = 1;
    this.searchRequest.startDate = null;
    this.searchRequest.endDate = null;
    this.searchRequest.showOnlyConfirmable = null;

    this.daySearchRequest.pageNumber = 1;
    this.daySearchRequest.startDate = null;
    this.daySearchRequest.endDate = null;
    this.daySearchRequest.showOnlyConfirmable = null;

    this.entrySearchRequest.pageNumber = 1;
    this.entrySearchRequest.dateFrom = null;
    this.entrySearchRequest.dateTo = null;

    this.updateList();
  }
}
